import { useState } from "react";
import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  useStore,
  useTranslate,
  type Exporter,
  type Identifier,
} from "ra-core";
import { useSearchParams } from "react-router";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { BulkDeleteButton } from "@/components/admin/bulk-delete-button";
import { ColumnsButton } from "@/components/admin/columns-button";
import { List } from "@/components/admin/list";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { SortButton } from "@/components/admin/sort-button";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  LayoutGrid,
  PanelLeftClose,
  PanelLeftOpen,
  Table2,
} from "lucide-react";

import type { Company, Contact, ContactNote, Sale, Tag } from "../types";
import { BulkTagButton } from "./BulkTagButton";
import { ContactCreateSheet } from "./ContactCreateSheet";
import { ContactEmpty } from "./ContactEmpty";
import { ContactListContentMobile } from "./ContactListContent";
import {
  ContactListFilterSummary,
  ContactListFilter,
} from "./ContactListFilter";
import { NeedsActionInput } from "./NeedsActionInput";
import { ContactShowSheet } from "./ContactShowSheet";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import { ColumnPreferencesProvider } from "../misc/ColumnPreferencesProvider";
import { AssignedToMeInput } from "../misc/AssignedToMeInput";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { ContactKanban } from "./kanban/ContactKanban";
import { KANBAN_PAGE_SIZE } from "./kanban/contactStages";
import { ContactTable } from "./table/ContactTable";
import { useContactResource } from "./contactResource";

type ContactViewMode = "table" | "kanban";

export const ContactList = () => {
  const resource = useContactResource();
  const { identity } = useGetIdentity();
  const [searchParams] = useSearchParams();
  const showId = searchParams.get("show");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode] = useStore<ContactViewMode>(`${resource}.viewMode`, "table");
  const isKanban = viewMode === "kanban";

  if (!identity) return null;

  return (
    <ColumnPreferencesProvider
      key={`${identity.id}:${resource}`}
      userId={identity.id}
      resource={resource}
    >
      <List
        title={false}
        actions={<ContactListActions onCreate={() => setCreateOpen(true)} />}
        // The Kanban is grouped into columns, not paged, so it must fetch
        // the full contact set (KANBAN_PAGE_SIZE) instead of the table's
        // regular page size; it also gets its own store slot for list
        // params (perPage/sort/filters).
        perPage={isKanban ? KANBAN_PAGE_SIZE : 25}
        pagination={isKanban ? null : undefined}
        sort={
          isKanban
            ? { field: "index", order: "ASC" }
            : { field: "last_seen", order: "DESC" }
        }
        storeKey={
          isKanban
            ? `${resource}.kanban.listParams.v2`
            : `${resource}.table.listParams.v2`
        }
        // Table and Kanban share one route/URL, only toggling a store flag.
        // Without this, ra-core prefers a non-empty URL query over the
        // per-view perPage/storeKey above, so the table's "rows per page"
        // (or a stale filter) leaks into the Kanban and caps/filters it.
        disableSyncWithLocation={isKanban}
        exporter={exporter}
      >
        <ContactListLayoutDesktop viewMode={viewMode} />
        <ContactShowSheet open={!!showId} id={showId ?? undefined} />
        <ContactCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      </List>
    </ColumnPreferencesProvider>
  );
};

const ContactListLayoutDesktop = ({
  viewMode,
}: {
  viewMode: ContactViewMode;
}) => {
  const resource = useContactResource();
  const { data, isPending, filterValues } = useListContext();
  const [filterPanelOpen] = useStore<boolean>(
    `${resource}.filterPanelOpen`,
    true,
  );

  const hasFilters = !!filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div className="flex flex-row gap-8">
      {filterPanelOpen && <ContactListFilter />}
      <div className="w-full flex flex-col gap-4">
        {viewMode === "kanban" ? (
          <ContactKanban />
        ) : (
          <Card className="py-0">
            <ContactTable />
          </Card>
        )}
      </div>
      {viewMode === "table" && (
        <BulkActionsToolbar>
          <ContactBulkActionButtons />
        </BulkActionsToolbar>
      )}
    </div>
  );
};

const ContactBulkActionButtons = () => (
  <>
    <SelectAllButton />
    <BulkTagButton />
    <BulkDeleteButton />
  </>
);

const ContactListActions = ({ onCreate }: { onCreate: () => void }) => {
  const translate = useTranslate();
  const resource = useContactResource();
  const [filterPanelOpen, setFilterPanelOpen] = useStore<boolean>(
    `${resource}.filterPanelOpen`,
    true,
  );
  const [viewMode, setViewMode] = useStore<ContactViewMode>(
    `${resource}.viewMode`,
    "table",
  );

  return (
    <TopToolbar>
      {resource === "contacts" && <AssignedToMeInput />}
      {viewMode === "table" && <NeedsActionInput />}
      <ToggleGroup
        type="single"
        value={viewMode}
        onValueChange={(value) =>
          value && setViewMode(value as ContactViewMode)
        }
        variant="outline"
      >
        <ToggleGroupItem
          value="table"
          aria-label={translate("crm.kanban.view_table", { _: "Table" })}
        >
          <Table2 className="size-4" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="kanban"
          aria-label={translate("crm.kanban.view_kanban", { _: "Kanban" })}
        >
          <LayoutGrid className="size-4" />
        </ToggleGroupItem>
      </ToggleGroup>
      {viewMode === "table" && (
        <>
          <SortButton fields={["first_name", "last_name", "last_seen"]} />
          <ColumnsButton />
        </>
      )}
      <Button
        variant="ghost"
        size="icon"
        aria-label={filterPanelOpen ? "Hide filters" : "Show filters"}
        onClick={() => setFilterPanelOpen(!filterPanelOpen)}
        className="cursor-pointer"
      >
        {filterPanelOpen ? (
          <PanelLeftClose className="size-4" />
        ) : (
          <PanelLeftOpen className="size-4" />
        )}
      </Button>
      <Button onClick={onCreate}>
        {translate(`resources.${resource}.action.new`)}
      </Button>
    </TopToolbar>
  );
};

export const ContactListMobile = () => {
  const resource = useContactResource();
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      storeKey={`${resource}.mobile.listParams.v2`}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const resource = useContactResource();
  const { isPending, data, error, filterValues } = useListContext();

  const hasFilters = !!filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader>
        <ContactListFilter />
      </MobileHeader>
      <MobileContent>
        {resource === "contacts" && <AssignedToMeInput />}
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (
  records,
  fetchRelatedRecords,
  dataProvider,
) => {
  const companies = await fetchRelatedRecords<Company>(
    records.map((contact) => ({
      ...contact,
      company_ids:
        contact.company_ids ??
        (contact.company_id == null ? [] : [contact.company_id]),
    })),
    "company_ids",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
  const tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");
  // contact.referred_by_name is denormalized on contacts_summary, but bulk
  // export (BulkExportButton) fetches records via getMany on the base
  // "contacts" resource, which doesn't carry summary-only fields -- so, like
  // company/sales above, re-resolve it explicitly rather than trust the spread.
  const referrers = await fetchRelatedRecords<Contact>(
    records,
    "referred_by_id",
    "contacts",
  );

  // Notes are 1-contact-to-many, so fetchRelatedRecords (keyed by a single
  // id per record) doesn't fit; fetch them all in one call instead. This is
  // a best-effort export of the two most recent notes as comment/comment_2
  // (the import counterpart of useContactImport) -- a contact with more than
  // two notes only gets the two most recent ones exported.
  const contactIds = records.map((contact) => contact.id);
  const { data: notes } = await dataProvider.getList<ContactNote>(
    "contact_notes",
    {
      filter: { "contact_id@in": `(${contactIds.join(",")})` },
      pagination: { page: 1, perPage: 10_000 },
      sort: { field: "date", order: "DESC" },
    },
  );
  const notesByContact = new Map<Identifier, ContactNote[]>();
  for (const note of notes) {
    const contactNotes = notesByContact.get(note.contact_id) ?? [];
    contactNotes.push(note);
    notesByContact.set(note.contact_id, contactNotes);
  }

  const contacts = records.map((contact) => {
    const recentNotes = notesByContact.get(contact.id) ?? [];
    const exportedContact = {
      ...contact,
      company_ids: undefined,
      companies: JSON.stringify(
        (
          contact.company_ids ??
          (contact.company_id == null ? [] : [contact.company_id])
        )
          .map((id) => companies[id]?.name)
          .filter(Boolean),
      ),
      company:
        contact.company_id != null
          ? companies[contact.company_id].name
          : undefined,
      zipcode:
        contact.company_id != null
          ? companies[contact.company_id].zipcode
          : undefined,
      city:
        contact.company_id != null
          ? companies[contact.company_id].city
          : undefined,
      referred_by_name:
        contact.referred_by_id != null
          ? `${referrers[contact.referred_by_id].first_name ?? ""} ${
              referrers[contact.referred_by_id].last_name ?? ""
            }`.trim()
          : undefined,
      sales:
        contact.sales_id != null
          ? `${sales[contact.sales_id].first_name} ${sales[contact.sales_id].last_name}`
          : undefined,
      tags: contact.tags.map((tagId) => tags[tagId].name).join(", "),
      email_work: contact.email_jsonb?.find((email) => email.type === "Work")
        ?.email,
      email_home: contact.email_jsonb?.find((email) => email.type === "Home")
        ?.email,
      email_other: contact.email_jsonb?.find((email) => email.type === "Other")
        ?.email,
      email_jsonb: JSON.stringify(contact.email_jsonb),
      email_fts: undefined,
      phone_work: contact.phone_jsonb?.find((phone) => phone.type === "Work")
        ?.number,
      phone_home: contact.phone_jsonb?.find((phone) => phone.type === "Home")
        ?.number,
      phone_other: contact.phone_jsonb?.find((phone) => phone.type === "Other")
        ?.number,
      phone_jsonb: JSON.stringify(contact.phone_jsonb),
      phone_fts: undefined,
      postal_code: contact.postal_code,
      // Named contact_city (not city) to avoid clashing with the company
      // address's own city field above (contact.city is the contact's own
      // address, unrelated to companies[contact.company_id].city).
      contact_city: contact.city,
      comment: recentNotes[0]?.text,
      comment_2: recentNotes[1]?.text,
    };
    delete exportedContact.email_fts;
    delete exportedContact.phone_fts;
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(
      csv,
      records[0]?.contact_type === "partner" ? "partners" : "contacts",
    );
  });
};
